import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Typography,
  Box,
  Link,
  Tooltip,
  Avatar
} from '@material-ui/core';
import PropTypes from 'prop-types';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import { get, map, find, round, toNumber, slice, isEmpty, size } from 'lodash';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpIcon from '@mui/icons-material/Help';
import clsx from 'clsx';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  '@keyframes pulsate': {
    '0%': {
      color: 'rgba(151,151,151, 0.2)'
    },
    '50%': {
      color: 'rgba(151,151,151, 1)'
    },
    '100%': {
      color: 'rgba(151,151,151, 0.2)'
    }
  },
  pulsating: {
    animation: '$pulsate 3s ease-in-out infinite'
  },
  oddRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  evenRow: {
    // backgroundColor: theme.palette.background.paper,
    height: '2rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.hover}40`
    }
  },
  tableHeader: {
    zIndex: 1
  },
  tableView: {
    borderRadius: '5px',
    overflow: 'auto'
  },
  tHeaderCol: {
    textAlign: 'center',
    // backgroundColor: '#f7f7f9',
    color: '#a6abbd',
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  tHeaderColStatic: {
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    height: '4rem',
    fontSize: '12px',
    fontWeight: '400'
  },
  tCol: {
    textAlign: 'center',
    color: theme.palette.background.dark,
    borderTop: '1px solid #cdd2d21A',
    fontSize: '12px'
  },
  teamDisplay: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  tBody: {
    marginTop: '-3px'
  },
  timeEntry: {
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  banner: {
    width: '100%',
    background: 'none'
  },
  profileLink: {
    // color: theme.palette.background.dark,
    fontWeight: '700',
    '&:hover': {
      color: theme.palette.text.primary
    },
    fontSize: '12px'
  },
  videoLink: {
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.tertiary.secondary
    },
    cursor: 'pointer'
  },
  hidden: {
    display: 'none'
  },
  tooltip: {
    fontSize: '12px'
  },
  characterImage: {
    backgroundColor: '#ebeef1',
    boxShadow: '0px 0px 1px 1px #cdd2d2',
    border: '1px solid #cdd2d2',
    height: '35px',
    width: '35px',
    margin: '0.1rem'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    marginBottom: '1rem',
    color: theme.palette.text.primary
  },
  details: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  text: {
    fontSize: '12px',
    color: '#8c96a2'
  },
  // empty: {
  //   display: 'flex',
  //   justifyContent: 'center',
  //   marginTop: '1rem',
  //   marginBottom: '1rem'
  // },
  breakdown: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    minWidth: '10rem'
  },
  ranking: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  highTier: {
    color: '#e29205 !important'
  },
  midTier: {
    color: '#1f8ecf !important'
  },
  lowTier: {
    color: '#2daf7f !important'
  },
  noTier: {
    color: '#737373 !important'
  },
  important: {
    fontWeight: '600',
    color: theme.palette.text.primary
  }
}));

function AbyssLeaderboardTable(props) {
  const classes = useStyles();
  const { characterAssets } = useSelector(state => state.gameAssets);
  const tableHeaderIds = map(props.headers, 'id');

  const rowsWithDetails = map(props.rows, row => ({
    ...row,
    details: find(
      props.details,
      detail => detail.competitor === get(row, 'competitor._id')
    ),
    asset: find(
      props.assets,
      asset => asset.competitor === get(row, 'competitor._id')
    )
  }));

  const breakdownCategories = [
    '12-1-1',
    '12-1-2',
    '12-2-1',
    '12-2-2',
    '12-3-1',
    '12-3-2'
  ];

  const detailTooltips = {
    most_used: 'These are the most used characters by this user',
    breakdown: 'A more detailed breakdown of player score in each chamber'
  };

  return (
    <>
      <Table className={classes.tableView} stickyHeader size="small">
        <TableHead className={classes.tableHeader}>
          <TableRow>
            {map(props.headers, header => {
              return (
                <TableCell
                  key={header.id}
                  id={header.id}
                  className={classes.tHeaderColStatic}
                >
                  {!!detailTooltips[header.id] && (
                    <Tooltip
                      classes={{ tooltip: classes.tooltip }}
                      title={detailTooltips[header.id]}
                    >
                      <Box classes={{ root: classes.details }}>
                        {header.label}
                        <Box mr={1} />
                        <HelpIcon style={{ color: '#ced3d3' }} />
                      </Box>
                    </Tooltip>
                  )}
                  {!detailTooltips[header.id] && header.label}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody className={classes.tBody}>
          {map(rowsWithDetails, (entry, idx) => {
            return (
              <TableRow
                className={clsx({
                  [classes.root]: true,
                  [classes.oddRow]: idx % 2,
                  [classes.evenRow]: !(idx % 2)
                })}
                key={`${entry._id}-${idx}`}
              >
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'rank'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'rank')
                  })}
                >
                  <Box className={classes.ranking}>
                    <Typography
                      classes={{
                        root: clsx({
                          [classes.text]: true,
                          [classes.highTier]: entry.rank <= 5,
                          [classes.midTier]: 5 < entry.rank && entry.rank <= 15,
                          [classes.lowTier]:
                            15 < entry.rank && entry.rank <= 30,
                          [classes.noTier]: 30 < entry.rank
                        })
                      }}
                      style={{ fontWeight: '600' }}
                    >
                      {entry.rank}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'rank'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'rank')
                  })}
                >
                  <Box className={classes.ranking}>
                    {toNumber(entry.rank) === toNumber(entry.previousRank) && (
                      <RemoveIcon
                        sx={{ color: 'white', marginLeft: '0.5rem' }}
                      />
                    )}
                    {toNumber(entry.rank) > toNumber(entry.previousRank) && (
                      <>
                        <KeyboardArrowDownIcon
                          sx={{ color: 'red', marginLeft: '0.5rem' }}
                        />
                        <Typography classes={{ root: classes.text }}>
                          {toNumber(entry.rank) - toNumber(entry.previousRank)}
                        </Typography>
                      </>
                    )}
                    {toNumber(entry.rank) < toNumber(entry.previousRank) && (
                      <>
                        <KeyboardArrowUpIcon
                          sx={{ color: 'green', marginLeft: '0.5rem' }}
                        />
                        <Typography classes={{ root: classes.text }}>
                          {toNumber(entry.previousRank) - toNumber(entry.rank)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'competitor'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'competitor'
                    )
                  })}
                >
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {!!get(entry, 'asset.profilePicture') && (
                      <Avatar
                        src={get(entry, 'asset.profilePicture')}
                        className={classes.characterImage}
                        style={{ marginRight: '1rem' }}
                      />
                    )}
                    <Link
                      href={`/public-profile?alias=${get(
                        entry,
                        'competitor.alias'
                      )}`}
                      underline="always"
                      target="_blank"
                    >
                      <Tooltip
                        title="View Profile"
                        classes={{ tooltip: classes.tooltip }}
                      >
                        <Typography classes={{ root: classes.profileLink }}>
                          {get(entry, 'competitor.alias')}
                        </Typography>
                      </Tooltip>
                    </Link>
                  </Box>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'region'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'region')
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {entry.region}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(tableHeaderIds, h => h === 'points'),
                    [classes.hidden]: !find(tableHeaderIds, h => h === 'points')
                  })}
                >
                  <Typography classes={{ root: classes.text }}>
                    {round(entry.points, 2)}
                  </Typography>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'most_used'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'most_used'
                    )
                  })}
                >
                  <AvatarGroup className={classes.teamDisplay} spacing="small">
                    {map(
                      slice(get(entry, 'details.characterUsage', []), 0, 3),
                      usage => {
                        return (
                          <Avatar
                            src={get(
                              characterAssets[usage.character],
                              'avatar'
                            )}
                            key={`${idx}-${usage.character}`}
                            className={classes.characterImage}
                          />
                        );
                      }
                    )}
                  </AvatarGroup>
                </TableCell>
                <TableCell
                  className={clsx({
                    [classes.tCol]: find(
                      tableHeaderIds,
                      h => h === 'breakdown'
                    ),
                    [classes.hidden]: !find(
                      tableHeaderIds,
                      h => h === 'breakdown'
                    )
                  })}
                >
                  <Box className={classes.breakdown}>
                    {map(breakdownCategories, (key, idx) => {
                      return (
                        <Box
                          key={`breakdown-${idx}`}
                          style={{
                            display: 'flex',
                            width: `${100 / size(breakdownCategories)}%`,
                            flexWrap: 'nowrap',
                            justifyContent: 'space-evenly'
                          }}
                        >
                          <Typography
                            style={{
                              width: 'fit-content',
                              marginRight: '0.5rem'
                            }}
                            classes={{
                              root: clsx({
                                [classes.text]: true,
                                [classes.important]:
                                  get(entry.breakdown, `${key}.rank`, 99) === 1
                              })
                            }}
                          >{`${key}: `}</Typography>
                          {get(entry.breakdown, `${key}.rank`) && (
                            <Tooltip title="Click to watch video">
                              <Link
                                onClick={props.loadVideo(
                                  get(entry, 'competitor.alias'),
                                  get(entry.breakdown, `${key}`)
                                )}
                                underline="hover"
                                target="_blank"
                              >
                                <Typography
                                  style={{
                                    width: 'fit-content'
                                  }}
                                  classes={{
                                    root: clsx({
                                      [classes.videoLink]: true,
                                      [classes.text]: true,
                                      [classes.important]:
                                        get(
                                          entry.breakdown,
                                          `${key}.rank`,
                                          99
                                        ) === 1,
                                      [classes.highTier]:
                                        get(
                                          entry.breakdown,
                                          `${key}.rank`,
                                          99
                                        ) <= 5,
                                      [classes.midTier]:
                                        5 <
                                          get(
                                            entry.breakdown,
                                            `${key}.rank`,
                                            99
                                          ) &&
                                        get(
                                          entry.breakdown,
                                          `${key}.rank`,
                                          99
                                        ) <= 15,
                                      [classes.lowTier]:
                                        15 <
                                          get(
                                            entry.breakdown,
                                            `${key}.rank`,
                                            99
                                          ) &&
                                        get(entry.rank, 'rank', 99) <= 30,
                                      [classes.noTier]:
                                        30 <
                                        get(entry.breakdown, `${key}.rank`, 99)
                                    })
                                  }}
                                  noWrap
                                >
                                  {get(entry.breakdown, `${key}.rank`)}
                                </Typography>
                              </Link>
                            </Tooltip>
                          )}
                          {!get(entry.breakdown, `${key}.rank`) && '-'}
                          <Box style={{ marginRight: '2rem' }}></Box>
                        </Box>
                      );
                    })}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {isEmpty(props.rows) && (
        <Box classes={{ root: classes.empty }}>
          <Typography variant="h4">No information to display.</Typography>
        </Box>
      )}
    </>
  );
}

AbyssLeaderboardTable.propTypes = {
  headers: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortDir: PropTypes.string.isRequired,
  onClickSort: PropTypes.func.isRequired,
  details: PropTypes.array.isRequired,
  loadVideo: PropTypes.func.isRequired,
  assets: PropTypes.array.isRequired
};

export default AbyssLeaderboardTable;
